import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Database, CheckCircle, Brain, Cog, Sparkles, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const Solutions = () => {
  const siteUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://orboroi.com';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Orboroi AI Data Operations Solutions",
    "description": "Comprehensive AI data services including annotation, validation, GenAI evaluation, and end-to-end model operations for production-ready AI systems.",
    "provider": {
      "@type": "Organization",
      "name": "Orboroi"
    },
    "areaServed": "Worldwide",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "AI Data Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Orboroi Annotate",
            "description": "Production-ready annotation for vision, multimodal, LiDAR, and radar data"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Orboroi Validate",
            "description": "Comprehensive model evaluation and testing services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Orboroi GenAI",
            "description": "Expert evaluation for LLMs, generative AI, and RLHF"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "End-to-End AI Ops",
            "description": "Complete AI operations from data preparation to deployment"
          }
        }
      ]
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Solutions", url: "/solutions" }
  ];

  const services = [
    {
      icon: Database,
      title: 'Orboroi Annotate',
      stage: 'Data Preparation',
      description: 'Production-ready annotation for vision, multimodal, LiDAR, and radar data. Our specialized teams deliver high-fidelity labels that meet the most demanding accuracy requirements.',
      features: [
        'Vision & multimodal annotation',
        'LiDAR and radar point cloud labeling',
        'Custom annotation guidelines',
        'Domain-specific expertise'
      ],
      microCase: {
        title: 'Autonomous Vehicles',
        result: '99% first-batch acceptance for sensor fusion data'
      },
      image: 'https://customer-assets.emergentagent.com/job_lifecycle-preview/artifacts/8q2c6boi_Orboroi%20Annotate.png',
      link: '/resources/multimodal-ai-data'
    },
    {
      icon: CheckCircle,
      title: 'Orboroi Validate',
      stage: 'Model Evaluation & Testing',
      description: 'Comprehensive QA and label verification to ensure your training data meets production standards. We catch errors before they impact model performance.',
      features: [
        'Multi-layer quality assurance',
        'Label verification and correction',
        'Edge case identification',
        'Performance benchmarking'
      ],
      microCase: {
        title: 'Medical AI',
        result: '10,000+ validated medical image segmentations'
      },
      image: 'https://customer-assets.emergentagent.com/job_lifecycle-preview/artifacts/bvfviai9_Orboroi%20Validate.png',
      link: '/resources/quality-metrics'
    },
    {
      icon: Brain,
      title: 'Orboroi GenAI',
      stage: 'Training & Continuous Improvement',
      description: 'RLHF, instruction tuning, and error detection for large language models and generative AI. Keep your models aligned with human preferences and production requirements.',
      features: [
        'Reinforcement Learning from Human Feedback (RLHF)',
        'Instruction tuning and fine-tuning',
        'Error detection and correction',
        'Continuous model improvement'
      ],
      microCase: {
        title: 'LLM Deployment',
        result: '40% reduction in hallucination rate post-RLHF'
      },
      image: 'https://customer-assets.emergentagent.com/job_lifecycle-preview/artifacts/b75qfzd8_Orboroi%20GenAI.png',
      link: '/resources/rlhf-best-practices'
    },
    {
      icon: Cog,
      title: 'End-to-End AI Operations',
      stage: 'Production & Scaling',
      description: 'Complete workforce management and operational support from pilot to production. Scale seamlessly with human-in-the-loop integration and ongoing quality monitoring.',
      features: [
        'Workforce management and scaling',
        'Pilot-to-production support',
        'Human-in-the-loop integration',
        'Ongoing quality monitoring'
      ],
      microCase: {
        title: 'Enterprise AI Platform',
        result: 'Scaled from 10 to 200+ annotators in 3 months'
      },
      image: 'https://customer-assets.emergentagent.com/job_lifecycle-preview/artifacts/qj3646pp_End-to-End%20AI%20Operations.png',
      link: '/resources/scaling-hitl-ai'
    },
    {
      icon: Sparkles,
      title: 'Custom AI Solutions',
      stage: 'Tailored for Your Needs',
      description: 'Custom pipelines and specialized services designed for your unique requirements. We adapt our expertise to solve your specific AI challenges.',
      features: [
        'Custom annotation pipelines',
        'Specialized domain expertise',
        'Flexible engagement models',
        'Dedicated team integration'
      ],
      microCase: {
        title: 'Sports Analytics',
        result: 'Real-time video annotation with embedded team'
      },
      image: 'https://customer-assets.emergentagent.com/job_lifecycle-preview/artifacts/bjlc407p_Custom%20AI%20Solutions.png',
      link: '/resources/annotation-workflows'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO
        title="AI Data Operations Solutions"
        description="Production-ready AI data services: annotation, validation, GenAI evaluation, and end-to-end model operations. Scale your AI with data certainty."
        canonical="/solutions"
        type="website"
        structuredData={structuredData}
        breadcrumbs={breadcrumbs}
      />
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Powering AI Models with <span className="text-[#379F7F]">Data, Validation, and Scale</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Orboroi provides production-ready data and evaluation services at every stage 
              of the AI model lifecycle, reducing risk and accelerating time-to-market.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-6">
          <div className="space-y-16">
            {services.map((service, index) => {
              // Map service titles to anchor IDs
              const anchorId = service.title === 'Orboroi Annotate' ? 'annotation'
                : service.title === 'Orboroi Validate' ? 'validate'
                : service.title === 'Orboroi GenAI' ? 'genai'
                : service.title === 'End-to-End AI Operations' ? 'operations'
                : '';
              
              return (
                <div 
                  key={index}
                  id={anchorId}
                  className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center scroll-mt-24`}
                >
                {/* Image */}
                <div className="lg:w-1/2">
                  <div className="relative rounded-lg overflow-hidden group">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <p className="text-[#379F7F] text-sm font-semibold mb-1">Case Study</p>
                      <p className="text-white font-bold text-lg">{service.microCase.title}</p>
                      <p className="text-gray-300 text-sm">{service.microCase.result}</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2">
                  <Card className="bg-gray-800 border-gray-700 hover:border-[#379F7F] transition-all duration-300">
                    <CardHeader>
                      <service.icon className="w-12 h-12 text-[#379F7F] mb-4" />
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs bg-[#379F7F] text-black px-2 py-1 rounded font-semibold">
                          {service.stage}
                        </span>
                      </div>
                      <CardTitle className="text-white text-2xl">{service.title}</CardTitle>
                      <CardDescription className="text-gray-300 text-base">
                        {service.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 mb-6">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-300">
                            <CheckCircle className="w-5 h-5 text-[#379F7F] mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Link 
                        to={service.link}
                        className="inline-flex items-center text-[#379F7F] hover:underline group"
                      >
                        Read more <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Schedule a Lifecycle Assessment
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              See how we can optimize your AI pipeline and reduce time-to-production. 
              Get a customized evaluation of your data needs and model requirements.
            </p>
            <Button 
              asChild
              size="lg"
              className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold text-lg px-8 py-6"
            >
              <Link to="/contact">Schedule Your Assessment</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
